import type { z } from 'zod';

import type { CurrencyModeSchema } from '@fragment/types/currencyCodes';

import { getSchemaObjectParameters } from '../parameterization';
import { SchemaCurrencyMatchInput } from '../types';

export type CurrencyValidationInput = {
  defaultCurrencyMode?: z.infer<typeof CurrencyModeSchema>;
  defaultCurrency?: { code: string; customCurrencyId?: string };
};

export type CurrencyValidationError = {
  message: string;
  path: (string | number)[];
};

export type CurrencyValidationResult = {
  errors: CurrencyValidationError[];
};

/**
 * Validates the combination of defaultCurrencyMode and defaultCurrency
 * Extracted from transformAndValidateCoA for reuse in frontend components
 */
export const validateDefaultCurrencySettings = ({
  defaultCurrencyMode: defaultCurrencyModeInput,
  defaultCurrency,
}: CurrencyValidationInput): CurrencyValidationResult => {
  const errors: CurrencyValidationError[] = [];

  // Determine and validate currency mode
  if (defaultCurrencyModeInput === undefined) {
    if (defaultCurrency === undefined) {
      errors.push({
        message: `defaultCurrencyMode or defaultCurrency must be provided`,
        path: [],
      });
    }
  } else if (defaultCurrencyModeInput === 'single') {
    // defaultCurrency is required for single mode
    if (defaultCurrency === undefined) {
      errors.push({
        message: `defaultCurrency must be provided for currency mode '${defaultCurrencyModeInput}'`,
        path: [`defaultCurrency`],
      });
    }
  } else if (defaultCurrencyModeInput === 'multi') {
    if (defaultCurrency !== undefined) {
      errors.push({
        message: `defaultCurrencyMode is 'multi', so defaultCurrency cannot be set`,
        path: [`defaultCurrency`],
      });
    }
  }

  // Validate currency format if provided
  if (defaultCurrency !== undefined) {
    // Default currency cannot use parameterized values — reject early
    const codeIsParameterized =
      getSchemaObjectParameters(defaultCurrency.code).length > 0;
    const customIdIsParameterized =
      !!defaultCurrency.customCurrencyId &&
      getSchemaObjectParameters(defaultCurrency.customCurrencyId).length > 0;

    if (codeIsParameterized) {
      errors.push({
        message: `Default currency code cannot be parameterized`,
        path: ['defaultCurrency', 'code'],
      });
    } else if (customIdIsParameterized) {
      errors.push({
        message: `Default currency customCurrencyId cannot be parameterized`,
        path: ['defaultCurrency', 'customCurrencyId'],
      });
    } else if (!SchemaCurrencyMatchInput.safeParse(defaultCurrency).success) {
      if (
        defaultCurrency.code === 'CUSTOM' &&
        !defaultCurrency.customCurrencyId
      ) {
        errors.push({
          message: `Must provide custom currency ID for custom currency`,
          path: ['defaultCurrency', 'customCurrencyId'],
        });
      } else if (
        defaultCurrency.customCurrencyId &&
        defaultCurrency.code !== 'CUSTOM'
      ) {
        errors.push({
          message: `customCurrencyId cannot be set when currency code is '${defaultCurrency.code}'`,
          path: ['defaultCurrency', 'customCurrencyId'],
        });
      } else if (defaultCurrency.code !== 'CUSTOM') {
        errors.push({
          message: `Default currency code '${defaultCurrency.code}' is invalid`,
          path: ['defaultCurrency', 'code'],
        });
      }
    }
  }

  return {
    errors,
  };
};
